import React, { useState } from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import { HashLink } from 'react-router-hash-link';
import {
  BackgroundBox,
  Icon,
  Info,
  FloatRight,
  PrintButton,
  SubTitle,
} from './basic';
import ServiceDetails from './ServiceDetails';
import ServiceChannel from './ServiceChannel';
import ContactInfoRow from './ContactInfoRow';
import BoxedLink from './BoxedLink';
import {
  faArrowUp,
  faSpinner,
  faPrint,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colors } from '../constants';
import { prepareMarkdown } from '../utils/helpers';
import FeedbackLink from './FeedbackLink';

const PrintOrgButton = styled(PrintButton).attrs({
  'aria-label': 'tulosta organisaation kuvaus',
})``;

export const MainInfo = styled.div`
  font-size: 1.2em;
  line-height: 2em;
  margin-bottom: 32px;
`;

const BackButtonPositioner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
`;

const Spinner = styled.div`
  font-size: 64px;
  text-align: center;
`;

const ShowAllButton = styled.button`
  display: inline-block;
  background: ${colors.primary};
  font: inherit;
  font-size: inherit;
  border: none;
  color: white;
  margin: 32px 0 0;
  padding: 4px 16px;
  font-weight: bold;
  cursor: pointer;
  &:hover {
    background: ${colors.primaryDark};
  }
  &:focus {
    background: ${colors.primaryDark};
  }
  @media print {
    display: none;
  }
`;

const ServiceWrapper = styled.div`
  border-top: 1px solid ${colors.primary};
  padding-top: 32px;
  margin: 48px 0;
  @media print {
    border: none;
    margin: 0;
    padding: 0;
  }
`;

const UpHashLink = styled(HashLink)`
  display: inline-block;
  border: 1px solid;
  padding: 4px 16px;
  font-weight: bold;
  cursor: pointer;
  &:hover,
  &:focus {
    text-decoration: none;
    background: ${colors.secondaryBackground};
  }
  @media print {
    display: none;
  }
`;

// Takes space for absolutely positioned BackButton
const SpaceReplacer = styled.div`
  height: 38px;
`;

const serviceViewOptions = {
  name: 'header',
  contact: 'full',
  summary: 'full',
  description: 'full',
  showChargeType: true,
};

const getAddresses = (addresses) =>
  addresses.map(
    (item, index) =>
      !!item.address && (
        <ContactInfoRow
          key={`${index}:${item.address}`}
          value={item.address}
          type="address"
        />
      ),
  );

const getEmails = (emails) =>
  emails.map(
    (item) =>
      !!item.email && (
        <ContactInfoRow
          key={item.email}
          value={item.email}
          info={item.info}
          type="email"
        />
      ),
  );

const getWebPages = (webPages) =>
  webPages.map(
    (item) =>
      !!item.url && (
        <ContactInfoRow
          key={item.url + '_' + item.info}
          value={item.url}
          info={item.info}
          type="link"
        />
      ),
  );

const getPhoneNumbers = (phoneNumbers) =>
  phoneNumbers.map(
    (item) =>
      !!item.number && (
        <ContactInfoRow
          key={item.number}
          value={item.number}
          info={item.info}
          type="phone"
        />
      ),
  );

const BackButton = () => (
  <BoxedLink to="/hakemisto/" back>
    Takaisin hakuun
  </BoxedLink>
);

// Main component
function OrganizationView({ apiHandler }) {
  // parse query string manually to support legacy IE
  const id = window.location.search
    .replace('?', '')
    .split('&')
    .find((item) => item.includes('id='))
    .replace('id=', '');

  const [showUnrelevant, setShowUnrelevant] = useState(
    false,
  );

  const [organization, setOrganization] = useState({});
  const [services, setServices] = useState([]);

  const {
    fetchOrganizationData,
    organizationData,
    filteredServices,
    status,
    serviceChannels,
    logOrganizationChange,
  } = apiHandler;

  window.onbeforeprint = (event) => {
    if (event.explicitOriginalTarget === window) {
      var override = document.createElement('style');
      override.setAttribute('id', 'override-print-styles');
      override.innerText =
        '* {user-select: auto;} @media print {.dont-print, .dont-print ~ .service-channel-box { display: block !important; }}';
      document.head.appendChild(override);
    }
  };

  window.onafterprint = (event) => {
    if (event.explicitOriginalTarget === window) {
      const override = document.getElementById(
        'override-print-styles',
      );
      override.remove();
    }
  };

  // Scroll and move focus to top when entering organization view
  useEffect(() => {
    document.getElementById('react-app').focus();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (id in organizationData) {
      setOrganization(organizationData[id]);
      logOrganizationChange(id, organizationData[id].name);
    }
    // Fetch organization data and/or service channels
    fetchOrganizationData(id);
  }, [
    organizationData,
    fetchOrganizationData,
    id,
    logOrganizationChange,
  ]);

  useEffect(() => {
    const result = filteredServices.filter((service) =>
      service.organizations.includes(id),
    );
    setServices(result);
  }, [filteredServices, id]);

  if (
    organization &&
    Object.keys(organization).length === 0
  )
    return status === 'error' ? (
      <>
        <SubTitle>
          Organisaation tietojen haku ei onnistunut
        </SubTitle>
        <BackButton />
      </>
    ) : (
      <Spinner>
        <FontAwesomeIcon
          icon={faSpinner}
          spin={true}
          color={colors.secondary}
        />
      </Spinner>
    );

  const renderServiceList = (serviceList) =>
    serviceList.map((item) => (
      <ServiceWrapper
        key={item.id}
        id={`palvelu-${item.id}`}
      >
        <ServiceDetails
          key={item.id}
          data={item}
          view={serviceViewOptions}
        />
        {serviceChannels
          .filter((sc) =>
            item.serviceChannels.includes(sc.id),
          )
          .map((serviceChannel, index) => (
            <ServiceChannel
              key={`${item.id}:${serviceChannel.id}`}
              data={serviceChannel}
              index={index}
            />
          ))}
        <UpHashLink to={`/organisaatio?id=${id}#palvelut`}>
          <Icon icon={faArrowUp} />
          Ylös
        </UpHashLink>
      </ServiceWrapper>
    ));

  const getServiceDetails = () =>
    renderServiceList(
      services.filter(
        (service) => service.showInOrganizationViews,
      ),
    );

  const getUnrelevantServiceDetails = () =>
    renderServiceList(
      services.filter(
        (service) => !service.showInOrganizationViews,
      ),
    );

  const renderTocItem = (item) => (
    <li key={item.id}>
      <HashLink
        to={`/organisaatio?id=${id}#palvelu-${item.id}`}
      >
        {item.name}
      </HashLink>
    </li>
  );

  const unrelevantHasLength =
    services.filter(
      (service) => !service.showInOrganizationViews,
    ).length > 0;

  function printSelections(evt) {
    const element = document.getElementById('organization');
    element.classList.remove('dont-print');
    window.print();
    element.classList.add('dont-print');
  }

  return (
    <div>
      <BackgroundBox
        id="organization"
        className="dont-print"
      >
        <BackButtonPositioner>
          <BackButton />
        </BackButtonPositioner>

        <FloatRight>
          <PrintOrgButton onClick={printSelections}>
            <Icon icon={faPrint} />
            Tulosta
          </PrintOrgButton>
        </FloatRight>

        <SpaceReplacer />
        <h1>{organization.name}</h1>
        <MainInfo>
          <div aria-label="kotisivu">
            {getWebPages(organization.webPages)}
          </div>
          <div aria-label="sähköposti">
            {getEmails(organization.emails)}
          </div>
          <div aria-label="puhelin">
            {getPhoneNumbers(organization.phoneNumbers)}
          </div>
          {!!organization.addresses && (
            <div aria-label="käyntiosoite">
              {getAddresses(organization.addresses)}
            </div>
          )}
          {!!organization.businessCode && (
            <Info>
              Y-tunnus: {organization.businessCode}
            </Info>
          )}
          <Info>{organization.summary}</Info>
        </MainInfo>
        <Info>
          {status === 'loading-extra' ? (
            <i>haetaan palveluita...</i>
          ) : (
            <>
              <div>
                <strong>Saatavilla olevat palvelut</strong>
              </div>
              {organization.availableServices
                .sort((a, b) => a.localeCompare(b))
                .join(', ')}
            </>
          )}
        </Info>
        {!!organization.description && (
          <Info
            dangerouslySetInnerHTML={{
              __html: prepareMarkdown(
                organization.description,
              ),
            }}
          />
        )}
      </BackgroundBox>
      <h2 id="palvelut">PALVELUT</h2>
      {status === 'ready' && (
        <>
          <ul className="palvelu-anchor-list">
            {services
              .filter(
                (service) =>
                  service.showInOrganizationViews,
              )
              .map(renderTocItem)}
          </ul>

          {showUnrelevant && (
            <ul className="palvelu-anchor-list">
              {services
                .filter(
                  (service) =>
                    !service.showInOrganizationViews,
                )
                .map(renderTocItem)}
            </ul>
          )}

          {!showUnrelevant && unrelevantHasLength && (
            <ShowAllButton
              onClick={() => setShowUnrelevant(true)}
            >
              Näytä kaikki palvelut
            </ShowAllButton>
          )}

          {getServiceDetails()}

          {showUnrelevant &&
            unrelevantHasLength &&
            getUnrelevantServiceDetails()}
        </>
      )}
      <div>
        <BackButton />
      </div>
      <FeedbackLink />
    </div>
  );
}

export default OrganizationView;
